import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";

export const putLockShipment = async ({ poNumber }: { poNumber: string }) => {
	const url = apiRoutes.PUTLOCKSHIPMENT.replace(":poNumber", poNumber);
	const resp = await putAndValidate(url, null, z.unknown());

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePutLockShipment = ({
	showNotifications = true,
}: {
	showNotifications?: boolean;
} = {}) => {
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		string
	>({
		mutationFn: (poNumber: string) => {
			if (!poNumber) {
				throw new Error("PO Number is required");
			}
			return putLockShipment({ poNumber });
		},
		onSuccess: () => {
			if (showNotifications) {
				showSuccess("Shipment locked.");
			}
		},
		onError: (err) => {
			if (showNotifications) {
				showError(
					err instanceof Error
						? err.message
						: "Failed to lock shipment. Please try again.",
				);
			}
		},
	});

	return {
		lockShipment: mutate,
		lockShipmentAsync: mutateAsync,
		isLockingShipment: isPending,
		error,
		data,
		resetError: reset,
	};
};
